import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { get, isArray, isEmpty, isNil, isObject } from 'lodash';
import { Button, CardActions, IconButton, MuiThemeProvider, OutlinedInput, Tooltip } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { toast } from 'react-toastify';
import { classes, style } from 'typestyle';
import validator from 'validator';

import type { ClientDataApi } from 'configuration-schemas/confdefnView';
import { Overlay } from 'src/common-ui';
import service from 'src/ServiceContainer';
import {
  zBasicConfigurableDataConfig,
  type BasicConfigurableDataConfig,
} from 'configuration-schemas/viewdefns/viewdefn';
import { TemplateModalTitle } from 'src/components/TemplateModalWithButton/TemplateModalWithButton';
import AcceptButton from 'src/components/AcceptButton/AcceptButton';
import RejectButton from 'src/components/RejectButton/RejectButton';
import styles, {
  modalContainer,
} from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/Editors/ConfigurableDataModal/ConfigurableDataModal.styles';
import { muiTheme } from 'src/utils/Style/Theme';
import ExtendedDataGrid from 'src/components/ExtendedDataGrid/ExtendedDataGrid';
import flowsheetStyles from 'src/pages/AssortmentBuild/FlowSheet/FlowSheetGrid.styles';
import { ReceiptStyles as receiptsStyles } from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/Editors/ReceiptsAdjCalculator.styles';
import { processApiParams } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.utils';
import type { BasicPivotItem, ListDataOptions } from 'src/worker/pivotWorker.types';
import { ASSORTMENT, externalGridSearchFields } from 'src/utils/Domain/Constants';
import {
  ColDef,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  IRowNode,
  SelectionChangedEvent,
  ValueGetterParams,
} from '@ag-grid-community/core';
import Renderer, { type RendererOptions } from 'src/utils/Domain/Renderer';
import { containsSearch } from 'src/utils/Pivot/Filter';
import { Indexable } from 'src/types/Primitive';
import acceptButton from 'src/components/AcceptButton/AcceptButton.styles';
import { InputCharacterWhitelist } from 'src/common-ui/components/Inputs/InputGeneric/InputGeneric';
import { pickByMaskParams } from 'src/components/StandardCardView/CardViewDataProcessor';

const COLUMN_WIDTH = 200;

function buildColumnDefs(config: BasicConfigurableDataConfig) {
  const colDefs = config.columns.map((col) => {
    const colDef: ColDef = {
      headerName: col.text,
      field: col.dataIndex,
      width: COLUMN_WIDTH,
      headerClass: [style({ padding: '0 10px !important' })],
      cellStyle: {
        justifyContent: 'unset',
        overflow: 'hidden',
        padding: '0 10px',
      },
      valueGetter: (params: ValueGetterParams) => {
        if (!params.data || params.data.rowType === 'group') return null;
        const size = params.data;
        const dataIndex = col.dataIndex ?? '';
        const field = size[dataIndex];
        return isObject(field) ? get(size, `${dataIndex}.value`, null) : size[dataIndex];
      },
      editable: false,
      // This is an internal S5 property used by datagrid
      // I don't know why this errors here and not in `EditableGrid.tsx`
      // @ts-ignore
      renderer: col.renderer ?? 'unknown',
      cellRendererSelector: (params: ICellRendererParams) => {
        if (!params.data || params.data.rowType === 'group') {
          return undefined;
        } else if (col.renderer && col.renderer in Renderer) {
          const rendererFn = Renderer[col.renderer as RendererOptions];
          return {
            // @ts-expect-error
            component: (props: any) => rendererFn(props.value),
          };
        } else {
          return {
            component: (props: any) => <div>{props.value}</div>,
          };
        }
      },
    };

    return colDef;
  });

  return colDefs;
}

function isSearchValid(
  search: string,
  whitelist: InputCharacterWhitelist = InputCharacterWhitelist.alphaNumericSeparators,
): boolean {
  const validatedSearch = validator.whitelist(search, whitelist);
  return validatedSearch.length > 0;
}

export interface BasicConfigurableDataModalProps {
  value: unknown;
  parentData: BasicPivotItem;
  configApi: ClientDataApi;
  floorset: string;
  isEditable: boolean; // certain views will not allow edits at all
  cellDataIndex: string; // used when returning after edit changes canceled
  renderTabs?: boolean;
  onSubmit: (selection: unknown) => void;
  onCancel: () => void;
}

/**
 * BasicConfigurableDataModal is a "lighter" implementation of ConfigurableDataModal
 * without the logic necessary to support receipts/sizing logic.
 *
 * Similar to TabbedReceipts modal, this component utilizes our DataGrid component to render data.
 */
export const BasicConfigurableDataModal = (props: BasicConfigurableDataModalProps) => {
  const { configApi, parentData, onCancel, onSubmit } = props;
  const [localValue, setLocalValue] = useState(props.value);
  const [searchValue, setSearchValue] = useState('');
  // by default we are loading because the config fetch is the first thing done
  const [isLoading, setIsLoading] = useState(true);
  const [config, setConfig] = useState<BasicConfigurableDataConfig>();
  const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
  const [modalData, setModalData] = useState<BasicPivotItem[]>([]);
  const [canSubmit, setCanSubmit] = useState(false);

  const gridApiRef = useRef<GridApi | null>(null);
  // required for external search to keep filter change handlers triggering by changes in search
  // and to keep the search state in sync
  const searchValueRef = useRef('');

  useEffect(() => {
    async function fetchData() {
      try {
        const defnId = new URLSearchParams(configApi.url.split('?')[1] ?? '').get('defnId');
        if (isNil(defnId)) {
          toast.error('An error occurred fetching the configuration for the modal');
          service.loggingService.error(`ConfigurableDataModal configApi.url is missing a defnId: ${configApi.url}`);
          onCancel();
          return;
        }

        const configResponse = await service.tenantConfigClient.getTenantViewDefns<BasicConfigurableDataConfig>({
          defnIds: [defnId],
          appName: ASSORTMENT,
          validationSchemas: [zBasicConfigurableDataConfig],
        });
        const config = configResponse[0];

        // setting config sooner than later renders the grid as soon as possible after columns are ready
        // the grid's loading prop takes over after that
        setConfig(config);
        const colDefs = buildColumnDefs(config);
        setColumnDefs(colDefs);

        const fetchHandler = config.dataHandlers.fetch;
        switch (fetchHandler.type) {
          case 'pivot': {
            const params = processApiParams(fetchHandler, parentData).params as ListDataOptions;
            if (isArray(params.topMembers)) {
              params.topMembers = params.topMembers.join(',');
            }

            const dataResponse = await service.pivotService.listData(fetchHandler.defnId, ASSORTMENT, params);
            if (dataResponse.tree.length == 0) {
              console.warn('No Data was returned for ConfigurableDataModal'); // eslint-disable-line no-console
            }

            // set grid columns/data in state and update in ag-grid instance
            setModalData(dataResponse.tree);
            break;
          }
          default:
            toast.error(
              'The modal is misconfigured and will be closed automatically.  Check the fetchHandler type for correctness',
            );
            service.loggingService.error(`Failed to fetch pivot for ConfigurableDataModal`);
            onCancel();
        }
      } catch (error) {
        const e = error as Error;
        toast.error('An error occurred fetching the configuration for the modal');
        service.loggingService.error(`An error occurred: ${e.message}`, e.stack);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearch = event.target.value;

    if (newSearch === '') {
      // user cleared input, won't pass validate function so clear manually
      setSearchValue(newSearch);
      return;
    }

    const isValid = isSearchValid(newSearch);
    if (isValid) {
      // sync state + ref value
      setSearchValue(newSearch);
      searchValueRef.current = newSearch;
      // notify grid so it can update filtered data
      gridApiRef.current?.onFilterChanged();
    } else {
      // leave it alone until valid
    }
  };

  const onClearSearch = () => {
    // sync state + ref value
    setSearchValue('');
    searchValueRef.current = '';
    // notify grid so it can update filtered data
    gridApiRef.current?.onFilterChanged();
  };

  const isExternalFilterPresent = useCallback((): boolean => {
    return !isEmpty(searchValueRef.current);
  }, []);

  const doesExternalFilterPass = useCallback((node: IRowNode<BasicPivotItem>): boolean => {
    let result = false;
    const searchInputs: string[] = [];

    // This logic was taken from `filterData` in src/utils/Pivot/Filter.ts

    searchValueRef.current.split(';').forEach((searchInput) => {
      if (searchInput.trim() !== '') searchInputs.push(searchInput.trim());
    });
    // If searchInputs is empty, user isn't searching
    if (isEmpty(searchInputs)) {
      result = true;
    } else if (node.data) {
      result = searchInputs.some((input) => containsSearch(node.data as Indexable, input, externalGridSearchFields));
    }
    return result;
  }, []);

  const onGridReady = (event: GridReadyEvent) => {
    gridApiRef.current = event.api;
    // have to wire these handlers up after the grid is loaded
    event.api.setGridOption('isExternalFilterPresent', isExternalFilterPresent);
    event.api.setGridOption('doesExternalFilterPass', doesExternalFilterPass);
  };

  const onSelectionChange = (_event: SelectionChangedEvent) => {
    let selection;
    const selectedModalRows = gridApiRef.current?.getSelectedRows() ?? [];
    const canSubmit = selectedModalRows.length > 0;

    if (canSubmit) {
      const selected = selectedModalRows[0];
      // TODO: Probably not needed, right now I have this matching the dataIndex in the column
      const updateDataIndex = config?.dataRules.move.updateDataIndex ?? '';
      const valueDataIndex = config?.dataRules.move.valueDataIndex ?? '';
      selection = selected[valueDataIndex];
    } else {
      // TODO: why is whitelist string | undefined when a default is provided in the zod type
      const whitelist = get(InputCharacterWhitelist, config?.dataRules.whitelist ?? '', 'alphaNumericSeparators');
      const validatedSearch = validator.whitelist(searchValue, whitelist);
      selection = validatedSearch;
    }

    setCanSubmit(canSubmit);
    setLocalValue(selection);
  };

  const cancel = () => {
    onCancel();
  };

  const submit = () => {
    onSubmit(localValue);
  };

  const create = () => {
    onSubmit(searchValue);
  };

  // only render a title after config is loaded to prevent flipping text after config is loaded
  const headerTitle = isLoading && !config ? '' : (config?.title ?? 'BasicConfigurableDataModal');

  // optional text rendered at the top of the sections container. Either a static string, or a
  // template rendered against the clicked object's data (parentData) via mask/maskParams.
  const topHeader = config?.dataSectionHeaders?.top;
  let topHeaderText: string | null = null;
  if (typeof topHeader === 'string') {
    topHeaderText = topHeader;
  } else if (topHeader) {
    const maskValues = pickByMaskParams(parentData, topHeader.maskParams);
    // @ts-expect-error picked mask values are a loose record, not a full BasicItem
    topHeaderText = Renderer.template(maskValues, topHeader.mask);
  }

  const displayedRowCount = gridApiRef.current?.getDisplayedRowCount() ?? 0;
  const canCreate = displayedRowCount === 0 && isSearchValid(searchValue);
  const tooltipTitle = canCreate
    ? `Click to create a new item with ID '${searchValueRef.current}'`
    : 'Enter a valid ID value to enable style creation';

  // Include section padding (28px), grid padding (16px), and room for the vertical scrollbar (16px).
  const modalWidth = (columnDefs.length || 2) * COLUMN_WIDTH + 28 + 16 + 16;

  return (
    <MuiThemeProvider theme={muiTheme}>
      <div className={styles.overlay}>
        <div
          data-qa="ag-popover"
          className={classes('basic-configurable-data-modal', modalContainer(modalWidth), styles.basicModalContainer)}
        >
          <div className={styles.header}>
            <TemplateModalTitle title={headerTitle} onCancel={onCancel} />
          </div>
          {isLoading && !config ? (
            <Overlay type="loading" visible={true} fitParent={true} />
          ) : (
            <Fragment>
              <div className={classes(styles.sectionsContainer, styles.basicSectionsContainer)}>
                {topHeaderText && <div className="top-header-text">{topHeaderText}</div>}
                <OutlinedInput
                  aria-label="Search data modal grid"
                  className={classes('form-control', styles.basicSearchInput)}
                  fullWidth={true}
                  endAdornment={
                    <IconButton size={'small'} onClick={onClearSearch} color="primary">
                      <Close color="primary" />
                    </IconButton>
                  }
                  value={searchValue}
                  onChange={onSearchChange}
                  placeholder="Search grid..."
                />
                <div className={classes(receiptsStyles.container, styles.basicGridContainer)}>
                  <ExtendedDataGrid
                    data={modalData}
                    columnDefs={columnDefs}
                    className={classes(
                      receiptsStyles.dataGridContainer,
                      flowsheetStyles.dataGridStyle,
                      receiptsStyles.modalGridOverrides,
                    )}
                    // renders the grid as soon as configs are loaded into state, data will updated dynamically
                    loaded={!isLoading}
                    rowHeight={35}
                    frameworkComponents={
                      {
                        // TODO: insert custom editors/renderers when needed
                      }
                    }
                    onGridReady={onGridReady}
                    extraAgGridProps={{
                      suppressMovableColumns: true,
                      rowSelection: 'single',
                      stopEditingWhenCellsLoseFocus: true,
                      groupDefaultExpanded: -1,
                      headerHeight: 40,
                      onSelectionChanged: onSelectionChange,
                    }}
                  />
                </div>
              </div>
              <CardActions classes={styles.actionButtons} disableSpacing>
                <Tooltip title={tooltipTitle}>
                  {/* span required to allow disabled buttons to contain a tooltip */}
                  <span>
                    <Button className={acceptButton} variant="contained" disabled={!canCreate} onClick={create}>
                      <i className="fal fa-2x fa-plus" aria-hidden="true" />
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    !canSubmit
                      ? 'To move an item, select items in the grid or enter a valid ID in the search to create an item'
                      : ''
                  }
                >
                  {/* span required to allow disabled buttons to contain a tooltip */}
                  <span>
                    <AcceptButton disabled={!canSubmit} onClick={submit} />
                  </span>
                </Tooltip>
                <RejectButton onClick={cancel} />
              </CardActions>
            </Fragment>
          )}
        </div>
      </div>
    </MuiThemeProvider>
  );
};
